import React, { useEffect, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { Modal, Field, Input, Button, Empty, Badge } from "@/components/ui-kit";
import { UserPlus, Trash2, Star, Users, ClipboardList, CalendarCheck, FileText } from "lucide-react";
import { fmtDate } from "@/lib/utils";
import { toast } from "sonner";

const SKILLS = [
  { key: "shooting", label: "Tir" },
  { key: "passing", label: "Passe" },
  { key: "dribbling", label: "Dribble" },
  { key: "defense", label: "Défense" },
  { key: "physical", label: "Physique" },
  { key: "tactical", label: "Tactique" },
];
const emptyEval = { shooting: 5, passing: 5, dribbling: 5, defense: 5, physical: 5, tactical: 5, note: "" };
const DAY_STATUS = [
  { key: "present", label: "Présent", cls: "bg-primary text-primary-foreground" },
  { key: "absent", label: "Absent", cls: "bg-destructive text-white" },
  { key: "excuse", label: "Excusé", cls: "bg-sky-500 text-white" },
];

export default function StageManager({ stage, open, onClose }) {
  const [tab, setTab] = useState("participants");
  const [roster, setRoster] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [skills, setSkills] = useState([]);
  const [part, setPart] = useState({ first_name: "", last_name: "", birthdate: "" });
  const [target, setTarget] = useState("");
  const [form, setForm] = useState(emptyEval);
  const [days, setDays] = useState([]);
  const [activeDay, setActiveDay] = useState("");
  const [dayRecords, setDayRecords] = useState({});

  const load = () => {
    if (!stage) return;
    api.get(`/events/${stage.id}/roster`).then((r) => setRoster(r.data));
    api.get(`/events/${stage.id}/participants`).then((r) => setParticipants(r.data));
    api.get(`/events/${stage.id}/skills`).then((r) => setSkills(r.data));
    api.get(`/events/${stage.id}/days`).then((r) => { setDays(r.data); if (r.data[0] && !activeDay) setActiveDay(r.data[0]); });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) load(); }, [open, stage]);

  useEffect(() => {
    if (!stage || !activeDay) return;
    api.get(`/events/${stage.id}/attendance`, { params: { date: activeDay } }).then((r) => {
      const map = {}; r.data.forEach((rec) => { map[rec.person_id] = rec.status; });
      setDayRecords(map);
    });
    /* eslint-disable-next-line */
  }, [activeDay, stage, participants.length]);

  const setDayStatus = (pid, st) => setDayRecords((m) => ({ ...m, [pid]: st }));
  const saveDay = async () => {
    const records = roster.map((p) => ({ person_id: p.id, kind: p.kind, status: dayRecords[p.id] })).filter((r) => r.status);
    await api.post(`/events/${stage.id}/attendance`, { date: activeDay, records });
    toast.success(`Présence du ${activeDay} enregistrée`);
  };

  const addPart = async (e) => {
    e.preventDefault();
    if (!part.first_name || !part.last_name) return;
    await api.post(`/events/${stage.id}/participants`, part);
    toast.success("Participant ajouté"); setPart({ first_name: "", last_name: "", birthdate: "" }); load();
  };
  const delPart = async (pid) => {
    if (!window.confirm("Retirer ce participant ?")) return;
    await api.delete(`/events/${stage.id}/participants/${pid}`); load();
  };

  const saveEval = async (e) => {
    e.preventDefault();
    if (!target) { toast.error("Choisissez un joueur"); return; }
    const [kind, id] = target.split(":");
    const payload = { stage_id: stage.id, ...form,
      ...SKILLS.reduce((a, s) => ({ ...a, [s.key]: parseInt(form[s.key]) }), {}) };
    if (kind === "member") payload.member_id = id; else payload.participant_id = id;
    await api.post("/skills", payload);
    toast.success("Évaluation de stage enregistrée"); setForm(emptyEval); load();
  };
  const delEval = async (sid) => { await api.delete(`/skills/${sid}`); load(); };

  const avg = (s) => (SKILLS.reduce((a, k) => a + (s[k.key] || 0), 0) / SKILLS.length).toFixed(1);

  return (
    <Modal open={open} onClose={onClose} title={`Stage — ${stage?.title || ""}`}>
      <div className="flex gap-2 mb-5">
        <button data-testid="stage-tab-participants" onClick={() => setTab("participants")}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${tab === "participants" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
          <Users size={14} /> Participants
        </button>
        <button data-testid="stage-tab-presence" onClick={() => setTab("presence")}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${tab === "presence" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
          <CalendarCheck size={14} /> Présence/jour
        </button>
        <button data-testid="stage-tab-eval" onClick={() => setTab("eval")}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${tab === "eval" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
          <ClipboardList size={14} /> Évaluations
        </button>
      </div>

      {tab === "participants" ? (
        <div>
          <p className="text-xs text-muted-foreground mb-3">Ajoutez un enfant <b>non affilié</b> au club, uniquement pour ce stage.</p>
          <form onSubmit={addPart} className="grid grid-cols-2 gap-2 mb-4">
            <Input data-testid="participant-firstname" placeholder="Prénom" value={part.first_name} onChange={(e) => setPart({ ...part, first_name: e.target.value })} />
            <Input data-testid="participant-lastname" placeholder="Nom" value={part.last_name} onChange={(e) => setPart({ ...part, last_name: e.target.value })} />
            <Button data-testid="add-participant-btn" className="col-span-2"><UserPlus size={15} /> Ajouter le participant externe</Button>
          </form>
          {participants.length ? (
            <div className="space-y-2">
              {participants.map((p) => (
                <div key={p.id} data-testid="participant-row" className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{p.first_name} {p.last_name}</span>
                    <Badge color="purple">Externe</Badge>
                  </div>
                  <button data-testid="del-participant-btn" onClick={() => delPart(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          ) : <Empty text="Aucun participant externe." />}
          <p className="text-xs text-muted-foreground mt-4">Affiliés du club concernés : <b>{roster.filter((r) => r.kind === "member").length}</b> (catégorie {stage?.category || "toutes"}).</p>
        </div>
      ) : tab === "presence" ? (
        <div>
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="flex gap-1.5 flex-wrap">
              {days.map((d) => (
                <button key={d} data-testid={`stage-day-${d}`} onClick={() => setActiveDay(d)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition-colors ${activeDay === d ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
                  {d.slice(8, 10)}/{d.slice(5, 7)}
                </button>
              ))}
            </div>
            <Button data-testid="export-stage-pdf" variant="outline" onClick={() => downloadFile(`/export/stage/${stage.id}.pdf`, "stage.pdf").catch(() => toast.error("Export échoué"))}><FileText size={14} /> PDF stage</Button>
          </div>
          {roster.length ? (
            <div className="space-y-2">
              {roster.map((p) => (
                <div key={p.id} data-testid="stage-att-row" className="flex items-center justify-between p-2.5 rounded-lg bg-secondary/50 gap-2">
                  <span className="font-semibold text-sm truncate">{p.name}{p.kind === "participant" ? " *" : ""}</span>
                  <div className="flex gap-1 shrink-0">
                    {DAY_STATUS.map((s) => (
                      <button key={s.key} data-testid={`stage-att-${s.key}-${p.id}`} onClick={() => setDayStatus(p.id, s.key)}
                        className={`px-2 py-1 rounded-md text-[11px] font-bold transition-colors ${dayRecords[p.id] === s.key ? s.cls : "bg-background text-muted-foreground hover:text-foreground"}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <Button data-testid="save-stage-day-btn" onClick={saveDay} className="mt-3">Enregistrer la présence du {activeDay?.slice(8, 10)}/{activeDay?.slice(5, 7)}</Button>
            </div>
          ) : <Empty text="Aucun joueur/participant pour ce stage." />}
        </div>
      ) : (
        <div>
          <form onSubmit={saveEval} className="mb-5">
            <Field label="Joueur / Participant">
              <select data-testid="stage-eval-target" value={target} onChange={(e) => setTarget(e.target.value)}
                className="w-full h-11 px-4 rounded-lg bg-secondary border border-border text-sm outline-none focus:border-primary">
                <option value="">Sélectionner...</option>
                {roster.map((r) => <option key={r.kind + r.id} value={`${r.kind}:${r.id}`}>{r.name}{r.external ? " (externe)" : ""}</option>)}
              </select>
            </Field>
            {SKILLS.map((s) => (
              <div key={s.key} className="mb-3">
                <div className="flex justify-between mb-1"><label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{s.label}</label><span className="text-primary font-bold text-sm">{form[s.key]}</span></div>
                <input data-testid={`stage-skill-${s.key}`} type="range" min="0" max="10" value={form[s.key]} onChange={(e) => setForm({ ...form, [s.key]: e.target.value })} className="w-full accent-[#22c55e]" />
              </div>
            ))}
            <Field label="Note"><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
            <Button data-testid="save-stage-eval-btn" className="w-full"><Star size={15} /> Enregistrer (évaluation stage)</Button>
          </form>
          <p className="text-xs text-muted-foreground mb-2">Ces évaluations sont <b>distinctes de la saison</b>.</p>
          {skills.length ? (
            <div className="space-y-2">
              {skills.map((s) => (
                <div key={s.id} data-testid="stage-eval-row" className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                  <div>
                    <span className="font-semibold text-sm">{s.person_name}</span>
                    {s.person_external && <Badge color="purple">Externe</Badge>}
                    <p className="text-xs text-muted-foreground">{fmtDate(s.date)} · Moyenne {avg(s)}/10</p>
                  </div>
                  <button data-testid="del-stage-eval-btn" onClick={() => delEval(s.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          ) : <Empty text="Aucune évaluation de stage." />}
        </div>
      )}
    </Modal>
  );
}
